import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { DIALOG_DATA, DialogService } from '../../../../services/dialog.service';
import { BehaviorSubject, defer, EMPTY, of, Subscription } from 'rxjs';
import { arr_count, arr_first, arr_map, blank, data_get, data_is_true, data_keys, data_nullify, fallback, filled, observe, OptionalValue, spf, transform } from '../../../../shared/utils/common';
import { AssetTypeImportTemplateOption } from './import-from-templates-button.component';
import { filter, switchMap } from 'rxjs/operators';
import { ApiService } from '../../../../services/api.service';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '../../../../services/notification.service';
import { FieldmagicDropdownOption } from '../../../../shared/components/forms/input/dropdown/dropdown.component';

@Component({
  selector: 'asset-types-update-from-template-button',
  template: `
    <fieldmagic-primary-button
      label="Import from template"
      icon="import"
      (click)="onClick()"
    >
    </fieldmagic-primary-button>
  `,
  providers: [
    DialogService,
  ]
})
export class AssetTypesUpdateFromTemplateButtonComponent {
  readonly updating$ = new BehaviorSubject<boolean>(false);

  @Input() id: string;

  @Output('completed') $onComplete = new EventEmitter<void>();

  constructor(
    private readonly _dialog: DialogService,
  ) { }

  onClick = () => observe({
    before: () => this.updating$.next(true),
    after: () => this.updating$.next(false),
    observable: () => defer(() => {
      if (blank(this.id)) {
        return EMPTY;
      }

      return this._dialog.show$({
        component: AssetTypesUpdateFromTemplateDialogComponent,
        data: {
          target_asset_type_id: this.id,
        },
      });
    }),
  })
    .pipe(
      filter((result) => filled(result) && data_is_true(data_get(result, 'completed')))
    )
    .subscribe((_) => this.$onComplete.emit());
}

@Component({
  selector: 'asset-types-update-from-template-dialog',
  template: `
    <fieldmagic-dialog
      label="Import from template"
      icon="import"
      [instance]="this"
    >
      <ng-template
        fieldmagicDialogButtonsTmp
      >
        <fieldmagic-primary-button
          icon="import"
          label="Import"
          (click)="onImport()"
          [isInProgress]="importing$ | async"
        >
        </fieldmagic-primary-button>
      </ng-template>

      <ng-template fieldmagicDialogContentTmp>
        <div
          *ngIf="(initializing$ | async) || (importing$ | async)"
          class="d-flex d-flex-gap flex-column align-items-center justify-content-center w-100"
        >
          <fieldmagic-icon [spin]="true"></fieldmagic-icon>
          <fieldmagic-text [content]="progress$ | async | translate"></fieldmagic-text>
        </div>

        <div
          *ngIf="(! (initializing$ | async) && ! (importing$ | async))"
          class="d-flex d-flex-gap flex-column w-100"
        >
          <fieldmagic-dropdown-input
            [(ngModel)]="template"
            label="Please select a standard template to use"
            placeholder="Please select a standard template to use"
            [options]="templates$ | async"
            [withRequiredMarker]="true"
            (ngModelChange)="onTemplateChange()"
          >
          </fieldmagic-dropdown-input>

          <ng-container *ngIf="template | filled">
            <fieldmagic-dropdown-input
              label="Asset Type"
              [(ngModel)]="assetType"
              [options]="assetTypes$ | async"
              [withRequiredMarker]="true"
              [errors]="errors | data_get: 'asset_type_id'"
            >
            </fieldmagic-dropdown-input>
          </ng-container>
        </div>
      </ng-template>
    </fieldmagic-dialog>
  `
})
export class AssetTypesUpdateFromTemplateDialogComponent {
  template?: OptionalValue<AssetTypeImportTemplateOption>;
  assetType?: OptionalValue<AssetTypeDropdownOption>;

  errors: Record<string, string[]> = {};

  readonly initializing$ = new BehaviorSubject<boolean>(true);
  readonly importing$ = new BehaviorSubject<boolean>(false);
  readonly progress$ = new BehaviorSubject<OptionalValue<string>>(null);

  readonly templates$ = new BehaviorSubject<AssetTypeImportTemplateOption[]>([]);

  readonly fetching$ = new BehaviorSubject<boolean>(false);
  readonly assetTypes$ = new BehaviorSubject<AssetTypeDropdownOption[]>([]);

  private _template$ = new BehaviorSubject<OptionalValue<AssetTypeImportTemplateOption>>(null);

  private _onTemplateFetchingSubscription?: Subscription;
  private _onTemplateChangeSubscription?: Subscription;

  constructor(
    @Inject(DIALOG_DATA) private readonly _data: AssetTypesUpdateFromTemplateDialogData,
    private readonly _api: ApiService,
    private readonly _translator: TranslateService,
    private readonly _notifications: NotificationService,
    private readonly _dialog: DialogService,
  ) { }

  ngOnInit(): void {
    this.progress$.next(this._translator.instant('Initializing....'));

    this._onTemplateFetchingSubscription = observe({
      after: () => this.initializing$.next(false),
      observable: () => this._api.callPrivate$({
        path: '/asset_types/get_standard_templates',
        expectsNewFormat: true,
        onSuccessResponse: (response) => response,
      })
    }).subscribe((response) => {
      const standards = fallback(response.get('standards'), {
        to: () => [],
      });
      const templates = arr_map(standards, {
        transformer: (standard) => ({
          id: standard.id,
          text: standard.text,
          encoded_name: standard.encoded_name,
        }),
      });

      this.templates$.next(templates);

      if (arr_count(templates) === 1) {
        this.template = arr_first(templates);
        this._template$.next(this.template);
      }
    });

    this._onTemplateChangeSubscription = this._template$.pipe(
      filter((template) => filled(template)),
      switchMap((template) => observe({
        before: () => this.fetching$.next(true),
        after: () => this.fetching$.next(false),
        observable: () => this._api.callPrivate$({
          path: '/asset_types/get_for_template',
          expectsNewFormat: true,
          body: {
            'standard_template': data_get(template, 'id'),
          },
          onSuccessResponse: (response) => response.get('items'),
        }),
      }))
    ).subscribe((assetTypes) => this.assetTypes$.next(arr_map(assetTypes, {
      transformer: (assetType) => ({
        id: data_get(assetType, 'id'),
        text: data_get(assetType, 'name'),
      }),
    })));
  }

  ngOnDestroy(): void {
    if (this._onTemplateFetchingSubscription) {
      this._onTemplateFetchingSubscription.unsubscribe();
    }

    if (this._onTemplateChangeSubscription) {
      this._onTemplateChangeSubscription.unsubscribe();
    }
  }

  onTemplateChange = () => this._template$.next(this.template);

  onImport = () => observe({
    before: () => this.importing$.next(true),
    after: () => this.importing$.next(false),
    observable: () => defer(() => {
      if (! this._isValid()) {
        return EMPTY;
      }

      return this._notifications
        .sendConfirmation('Importing from a standard template will override the values for this asset type. Are you sure you want to proceed?')
        .pipe(
          filter((confirmation) => confirmation.answer),
          switchMap(() => {
            this.progress$.next(spf('Importing %s...', {
              args: [this._translator.instant(this._getTableLabel('asset_types'))],
            }));

            return this._import();
          }),
        );
    })
  }).subscribe(() => {
    this._notifications.notifySuccess('Imported successfully.');
    this._dialog.close<AssetTypesUpdateFromTemplateDialogComponent, AssetTypesUpdateFromTemplateDialogResult>({
      instance: this,
      result: {
        completed: true,
      },
    });
  });

  private _import(args: {
      state?: string;
    } = {}) {
      return this._api.callPrivate$({
        path: '/asset_types/import_from_standard_template',
        expectsNewFormat: true,
        body: {
          standard_template: data_get(this.template, 'id'),
          standard_template_name: data_get(this.template, 'encoded_name'),
          should_exclude_ids: false,
          ... (filled(data_nullify(args.state)) && {
            next_page: args.state,
          }),
          ids: [this.assetType.id],
          asset_type_id: this._data.target_asset_type_id,
          as_replacement: true,
        },
        onSuccessResponse: (response) => ({
          state: response.get('next_page'),
          next_table: response.get('next_table'),
        }),
      }).pipe(
        switchMap((data) => {
          if (blank(data.state)) {
            return of(null);
          }

          this.progress$.next(spf(this._translator.instant('Importing %s...'), {
            args: [this._translator.instant(this._getTableLabel(data.next_table))],
          }));

          return this._import({
            state: data.state,
          });
        }),
      );
    }

  private _isValid(): boolean {
    const errors = {};

    if (blank(this.assetType)) {
      errors['asset_type_id'] = ['field_is_required'];
    }

    this.errors = errors;

    return blank(errors);
  }

  private _getTableLabel(table: string): string {
    if (table === 'asset_types') {
      return 'Asset Types';
    }

    if (table === 'asset_type_variants') {
      return 'Variants';
    }

    if (table === 'asset_type_possible_faults') {
      return 'Defects';
    }

    if (table === 'asset_type_inspection_checklists') {
      return 'Inspection Checklists';
    }

    return 'Unknown';
  }
}

type AssetTypeDropdownOption = FieldmagicDropdownOption<{
  id: string;
}>;

type AssetTypesUpdateFromTemplateDialogData = {
  target_asset_type_id: string;
};

type AssetTypesUpdateFromTemplateDialogResult = {
  completed: boolean;
};